import MIcon from '../../components/common/MIcon';
import { useT } from '../../context/TenantContext';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { useDeviceInfo } from '../../hooks/useDeviceInfo';
import { LogoUNEA } from '../../components/common/Logo';
import Icon from '../../components/common/Icon';
import { Button } from '../../components/ui';
import PortalNotice from '../../components/common/PortalNotice';
import TrackForm from '../../components/common/TrackForm';
import WatcherEmailsField from '../../components/common/WatcherEmailsField';
import { FloatInput, FloatSelect, FloatTextarea } from '../../components/common/FloatField';
import FdDropBar from './FdDropBar';
import { useFrontDeskAuth } from '../../context/FrontDeskAuthContext';
import FrontDeskLogin from './FrontDeskLogin';
import './frontdesk.css';

// Subcategorías que son peticiones estructuradas (no incidencias): no tiene
// sentido pedir "describe el problema" ni "impacto en tu trabajo" para ellas.
const PETITION_SUBCATS = [
  'Alta de usuario', 'Baja de usuario', 'Suspensión de cuenta', 'Cambio de contraseña', 'Modificación de permisos',
  'Alta/baja de extensión',
  'Videollamada/Videoconferencia', 'Examen con vigilancia', 'Presentación/Ponencia', 'Otro evento',
];

// Oculto temporalmente en todo el portal — poner a true para reactivarlo
const SHOW_DEVICE_INFO = false;

const CATEGORIES = {
  'Hardware':        ['Equipo/Ordenador','Impresora','Monitor','Teclado/Ratón','Móvil/Tablet','Otro hardware'],
  'Red':             ['Sin internet','WiFi','VPN/Acceso remoto','Red lenta','Otro problema de red'],
  'Software':        ['Sistema operativo','Microsoft Office','Aplicación específica','Error o fallo','Otro software'],
  'Accesos':         ['Contraseña olvidada','Cuenta bloqueada','Sin permisos','Alta de usuario','Baja de usuario','Suspensión de cuenta','Cambio de contraseña','Modificación de permisos','Otro acceso'],
  'Servidores':      ['Servidor caído','Virtualización','Backup/Copias','Base de datos','Otro servidor'],
  'Comunicaciones':  ['Email corporativo','Alta/baja de extensión','Videoconferencia','Otro'],
  'Solicitud':       ['Instalar software','Nuevo hardware','Formación','Cambio configuración','Otro'],
  'Eventos':         ['Videollamada/Videoconferencia','Examen con vigilancia','Presentación/Ponencia','Otro evento'],
};

// Campos compartidos por las subcategorías de "Eventos" — misma info en todas
const EVENT_FIELDS = [
  { key:'event_room',      label:'Sala/ubicación',        type:'text', placeholder:'Ej. Sala de Juntas, Aula 3B…' },
  { key:'event_date',      label:'Fecha del evento',      type:'date' },
  { key:'event_start',     label:'Hora de inicio',        type:'time' },
  { key:'event_end',       label:'Hora de fin',           type:'time' },
  { key:'event_attendees', label:'Nº asistentes aprox.',  type:'number', placeholder:'Ej. 20' },
  { key:'event_needs',     label:'Necesidades técnicas',  type:'text', placeholder:'Ej. Videollamada, proyector, micrófono, vigilancia de examen…' },
];

// Campos extra por subcategoría
const EXTRA_FIELDS = {
  'Impresora': [
    { key:'toner_color',       label:'Color del tóner',        type:'select', options:['Negro','Cian','Magenta','Amarillo','Todos'] },
    { key:'printer_location',  label:'Ubicación de la impresora', type:'text', placeholder:'Ej. Planta 2, Sala de reuniones B…' },
  ],
  'Teclado/Ratón': [
    { key:'peripheral_type', label:'Tipo de periférico', type:'select', options:['Teclado','Ratón','Ambos'] },
    { key:'connection', label:'Conexión', type:'select', options:['USB','Bluetooth','Inalámbrico','Otro'] },
  ],

  'Equipo/Ordenador': [
    { key:'device_type', label:'Tipo de equipo', type:'select', options:['Sobremesa','Portátil','Todo en uno','Servidor'] },
    { key:'os_version', label:'Sistema operativo', type:'select', options:['Windows 10','Windows 11','macOS','Linux','Otro'] },
  ],
  'VPN/Acceso remoto': [
    { key:'vpn_client', label:'Cliente VPN', type:'select', options:['FortiClient','GlobalProtect','Cisco AnyConnect','Otro'] },
    { key:'remote_location', label:'Ubicación de acceso', type:'text', placeholder:'Ej. Casa, Hotel, etc.' },
  ],
  'Instalar software': [
    { key:'software_name', label:'Nombre del software', type:'text', placeholder:'Ej. Adobe Reader, AutoCAD...' },
    { key:'license_type', label:'Tipo de licencia', type:'select', options:['Institucional','Personal','Gratuito','No sé'] },
  ],
  'Nuevo hardware': [
    { key:'hardware_type', label:'Tipo de hardware', type:'select', options:['Ordenador','Monitor','Teclado/Ratón','Impresora','Cable','Tóner','Otro'] },
    { key:'quantity', label:'Cantidad', type:'text', placeholder:'Ej. 1' },
  ],
  'Email corporativo': [
    { key:'email_client', label:'Cliente de correo', type:'select', options:['Gmail','Outlook','Apple Mail','Thunderbird','Otro'] },
    { key:'email_issue', label:'Tipo de problema', type:'select', options:['No envía','No recibe','Configuración','Spam','Otro'] },
  ],
  'Alta de usuario': [
    { key:'new_user_name',   label:'Nombre completo de la persona', type:'text', placeholder:'Ej. María López García' },
    { key:'new_user_dept',   label:'Departamento',                  type:'text', placeholder:'Ej. Administración' },
    { key:'new_user_access', label:'Accesos necesarios',            type:'text', placeholder:'Ej. Correo, carpeta compartida Finanzas, ERP…' },
    { key:'new_user_date',   label:'Fecha de incorporación',        type:'date' },
  ],
  'Baja de usuario': [
    { key:'off_user_name', label:'Nombre completo de la persona', type:'text', placeholder:'Ej. María López García' },
    { key:'off_user_dept', label:'Departamento',                  type:'text', placeholder:'Ej. Administración' },
    { key:'off_user_date', label:'Fecha de baja',                 type:'date' },
  ],
  'Suspensión de cuenta': [
    { key:'susp_user_name', label:'Nombre completo de la persona', type:'text', placeholder:'Ej. María López García' },
    { key:'susp_reason',    label:'Motivo de la suspensión',       type:'text', placeholder:'Ej. Baja temporal, sospecha de uso indebido…' },
    { key:'susp_until',     label:'Hasta cuándo (si se sabe)',     type:'date' },
  ],
  'Cambio de contraseña': [
    { key:'pwd_user_name', label:'Nombre completo de la persona', type:'text', placeholder:'Ej. María López García' },
    { key:'pwd_reason',    label:'Motivo',                        type:'select', options:['Posible cuenta comprometida','Solicitud del usuario','Política de renovación','Otro'] },
  ],
  'Modificación de permisos': [
    { key:'mod_user_name',   label:'Nombre completo de la persona', type:'text', placeholder:'Ej. María López García' },
    { key:'mod_user_access', label:'Accesos a añadir/quitar',       type:'text', placeholder:'Ej. Añadir carpeta RRHH, quitar acceso ERP…' },
  ],
  'Alta/baja de extensión': [
    { key:'ext_type',     label:'Tipo de solicitud', type:'select', options:['Alta nueva','Baja','Cambio de ubicación'] },
    { key:'ext_name',     label:'Nombre y departamento', type:'text', placeholder:'Ej. Juan Pérez, Secretaría' },
    { key:'ext_location', label:'Ubicación (despacho/sala)', type:'text', placeholder:'Ej. Edificio A, planta 2, despacho 204' },
  ],
  'Videollamada/Videoconferencia': EVENT_FIELDS,
  'Examen con vigilancia':         EVENT_FIELDS,
  'Presentación/Ponencia':         EVENT_FIELDS,
  'Otro evento':                   EVENT_FIELDS,
  'Sin internet': [
    { key:'connection_type', label:'Tipo de conexión', type:'select', options:['WiFi','Cable ethernet','Ambas'] },
    { key:'affected_devices', label:'Equipos afectados', type:'select', options:['Solo el mío','Varios equipos','Toda la planta','Toda la sede'] },
  ],
};

const IMPACT_COLORS = [
  { v:1, color:'#B91C1C', bg:'#FEF2F2', border:'#FECACA', dot:'#B91C1C' },
  { v:2, color:'#B45309', bg:'#FFFBEB', border:'#FDE68A', dot:'#B45309' },
  { v:3, color:'var(--fd-accent)', bg:'var(--una-blue-lt)', border:'#BFDBFE', dot:'var(--fd-accent)' },
  { v:4, color:'#15803D', bg:'#F0FDF4', border:'#86EFAC', dot:'#15803D' },
];

const FD_LANGS = [
  { code: 'es', flag: '🇪🇸' },
  { code: 'pt', flag: '🇦🇴' },
  { code: 'en', flag: '🇬🇧' },
];

export default function FrontDesk() {
  const { t } = useT();
  const IMPACT_INFO = [
    { ...IMPACT_COLORS[0], label:t('fd.impact1_label'), desc:t('fd.impact1_desc') },
    { ...IMPACT_COLORS[1], label:t('fd.impact2_label'), desc:t('fd.impact2_desc') },
    { ...IMPACT_COLORS[2], label:t('fd.impact3_label'), desc:t('fd.impact3_desc') },
    { ...IMPACT_COLORS[3], label:t('fd.impact4_label'), desc:t('fd.impact4_desc') },
  ];
  const navigate = useNavigate();
  const { fdUser, fdLogout, canAccess, isInternal, fdLoading } = useFrontDeskAuth();

  // Autocompletar nombre y email desde Google cuando fdUser está disponible
  useEffect(() => {
    if (fdUser) {
      setForm(f => ({
        ...f,
        requester:       f.requester       || fdUser.name  || '',
        requester_email: f.requester_email || fdUser.email || '',
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fdUser?.email]);

  // Detectar error de autenticación en URL
  const [authError, setAuthError] = React.useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('auth_error') || null;
  });
  const { info: deviceInfo, status: deviceStatus } = useDeviceInfo();

  const [step,    setStep]    = useState(1);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [created, setCreated] = useState(null);

  const [errorToast,   setErrorToast]   = useState('');
  const [aiLoading,    setAiLoading]    = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiApplied,    setAiApplied]    = useState(false);
  const classifyTimer = useRef(null);

  const [form, setForm] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      title:'', description:'',
      category: p.get('category') || '', subcategory: p.get('subcategory') || '',
      impact: null, priority:'medium', requester:'', requester_email:'', watcher_emails:'', tags:[],
      extra_fields: {},
    };
  });
  // Si category/subcategory llegaron por la URL (desde una card de RequestHome),
  // no se muestran como campos editables — el usuario ya eligió esto. Son dos
  // flags independientes: una card puede fijar solo la categoría y dejar la
  // subcategoría como selector (ej. "Altas y bajas de cuentas": fija Accesos,
  // pero el usuario elige entre alta/baja/suspensión/cambio de contraseña).
  const [prefilledCategory]    = useState(() => !!new URLSearchParams(window.location.search).get('category'));
  const [prefilledSubcategory] = useState(() => !!new URLSearchParams(window.location.search).get('subcategory'));
  // Valores originales de la URL — "Limpiar" debe volver a esto, no a vacío,
  // si no el usuario se queda sin categoría y sin selector para elegir otra.
  const [originalCategory]    = useState(() => new URLSearchParams(window.location.search).get('category') || '');
  const [originalSubcategory] = useState(() => new URLSearchParams(window.location.search).get('subcategory') || '');
  const isPetition = PETITION_SUBCATS.includes(form.subcategory);
  // El impacto tampoco aplica a nada de Accesos (contraseña olvidada, cuenta
  // bloqueada, etc.) — no solo a las peticiones estructuradas.
  const hideImpact = isPetition || form.category === 'Accesos';
  const [slaData, setSlaData] = useState(null);
  const [attachFiles, setAttachFiles] = useState([]);

  useEffect(() => {
    fetch('/api/sla/estimated').then(r=>r.json()).then(data => {
      const map = {};
      data.forEach(d => { map[d.priority] = d; });
      setSlaData(map);
    }).catch(()=>{});
  }, []);

  function set(field, val) {
    if (field === 'category') { setForm(f => ({ ...f, category: val, subcategory:'', extra_fields:{} })); return; }
    if (field === 'subcategory') { setForm(f => ({ ...f, subcategory: val, extra_fields:{} })); return; }
    if (field === 'title') { setAiSuggestion(null); setAiApplied(false); }
    setForm(f => ({ ...f, [field]: val }));
  }

  useEffect(() => {
    clearTimeout(classifyTimer.current);
    if (form.title.length < 10) return;
    classifyTimer.current = setTimeout(async () => {
      setAiLoading(true);
      try {
        const res = await fetch('/api/classify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: form.title, description: form.description }),
        });
        const data = await res.json();
        if (data.ok) setAiSuggestion(data);
      } catch {}
      finally { setAiLoading(false); }
    }, 1500);
    return () => clearTimeout(classifyTimer.current);
  }, [form.title, form.description]);

  function applyAI() {
    if (!aiSuggestion) return;
    setForm(f => ({
      ...f,
      category:    aiSuggestion.category    || f.category,
      subcategory: aiSuggestion.subcategory || f.subcategory,
      impact:      aiSuggestion.impact      || f.impact,
      priority:    aiSuggestion.priority    || f.priority,
      title:       aiSuggestion.suggested_title || f.title,
      tags:        aiSuggestion.tags?.length ? aiSuggestion.tags : f.tags,
    }));
    setAiApplied(true);
  }

  async function submit(e) {
    e.preventDefault();
    setErrorToast('');
    if (!isPetition && !form.title) return setErrorToast(t('fd.required'));
    if (!form.requester) return setErrorToast(t('fd.required'));
    if (!form.requester_email) return setErrorToast('El email de contacto es obligatorio.');
    if (!hideImpact && !form.impact) return setErrorToast('Indica el impacto que tiene el problema en tu trabajo.');
    setLoading(true);
    try {
      const priorityMap = { 1:'critical', 2:'high', 3:'medium', 4:'low' };
      const finalPriority = hideImpact ? 'medium' : (form.priority || priorityMap[form.impact]);
      const finalTitle = form.title || `${form.subcategory} — ${form.requester}`;
      const ticket = await api.createTicket({
        ...form,
        title: finalTitle,
        priority: finalPriority,
        watcher_emails: form.watcher_emails.split(',').map(e => e.trim()).filter(Boolean),
        device_info: deviceInfo || null,
        tags: [...(form.tags||[]), form.category, form.subcategory].filter(Boolean),
        extra_fields: form.extra_fields,
      });
      // Upload attachments if any
      if (attachFiles.length > 0) {
        const fd = new FormData();
        attachFiles.forEach(f => fd.append('files', f));
        await api.uploadAttachments(ticket.id, fd);
      }
      // Redirigir directamente al TrackTicket
      navigate(`/ticket/${ticket.ref}`, { state: { showToast: true } });
    } catch (err) {
      setErrorToast(err.message);
    } finally {
      setLoading(false);
    }
  }

  const fmtMin = (m) => m < 60 ? `${m}m` : m < 1440 ? `${Math.round(m/60*10)/10}h` : `${Math.round(m/1440*10)/10}d`;
  const selectedImpact = IMPACT_INFO.find(i => i.v === form.impact);

  // Esperar a que se resuelva el network-check antes de montar el formulario
  if (fdLoading) return null;

  // Guard: mostrar login si está fuera de la red y no tiene sesión Google
  if (!canAccess) return <FrontDeskLogin error={authError} />;

  if (step === 2 && created) return (
    <SuccessView
      ticket={created}
      onNew={() => { setStep(1); setCreated(null); setForm({ title:'', description:'', category:originalCategory, subcategory:originalSubcategory, impact:null, priority:'medium', requester:'', requester_email:'', watcher_emails:'', tags:[], extra_fields:{} }); setAiSuggestion(null); }}
      onTrack={() => navigate(`/ticket/${created.ref}`, { state: { showToast: true } })}
    />
  );

  return (
    <div className="fd-root">
      {aiApplied && <AiToast onDone={() => { setAiApplied(false); setAiSuggestion(null); }} />}
      {errorToast && <ErrorToast msg={errorToast} onDone={() => setErrorToast('')} />}
      <header className="fd-appbar">
        <div style={{display:'flex',alignItems:'center',gap:0}}>
          <LogoUNEA height={32} style={{display:'block'}} />
          <div style={{width:1,height:28,background:'var(--fd-border)',margin:'0 16px'}}/>
          <span style={{fontSize:15,fontWeight:400,color:'var(--fd-text)'}}>HelpDesk TI</span>
        </div>
        {/* Selector idioma */}
        <div style={{display:'flex',alignItems:'center',gap:4,position:'relative'}}>
          {FD_LANGS.map(l => (
            <button key={l.code} onClick={() => { localStorage.setItem('bd_lang_override', l.code); window.location.reload(); }}
              title={l.code.toUpperCase()}
              style={{
                padding:'4px 7px', borderRadius:7, border:'1px solid',
                borderColor: (localStorage.getItem('bd_lang_override')||'es') === l.code ? 'var(--fd-accent)' : 'transparent',
                background: (localStorage.getItem('bd_lang_override')||'es') === l.code ? 'var(--una-blue-lt)' : 'transparent',
                cursor:'pointer', fontSize:18, lineHeight:1, opacity: (localStorage.getItem('bd_lang_override')||'es') === l.code ? 1 : 0.5,
              }}>
              {l.flag}
            </button>
          ))}
        </div>
        {/* Usuario Google */}
        {fdUser && (
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            {fdUser.picture
              ? <img src={fdUser.picture} alt="" style={{width:30,height:30,borderRadius:'50%',border:'2px solid #C5D8E8'}}/>
              : <div style={{width:30,height:30,borderRadius:'50%',background:'#0085CA',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:700}}>{fdUser.name?.[0]?.toUpperCase()}</div>
            }
            <span style={{fontSize:13,color:'var(--fd-muted)',fontWeight:500}}>{fdUser.name}</span>
            <button onClick={fdLogout}
              style={{fontSize:12,color:'var(--fd-muted)',background:'none',border:'none',cursor:'pointer',padding:'4px 8px',borderRadius:6,transition:'color .12s'}}
              onMouseOver={e=>e.currentTarget.style.color='#B91C1C'}
              onMouseOut={e=>e.currentTarget.style.color='var(--fd-muted)'}>
              Cerrar sesión
            </button>
          </div>
        )}
      </header>
      <PortalNotice />

      <div className="fd-two-col">

        {/* ── COLUMNA IZQUIERDA — dos cards apilados ── */}
        <div className="fd-col-left">

          {/* Card superior: hero + consultar ticket */}
          <div className="fd-card fd-card-side">
          <h1 className="fd-side-title">
            ¿Cómo podemos<br/>
            <em style={{fontStyle:'normal',color:'var(--fd-accent)'}}>{t('fd.hero_accent')}</em>
          </h1>
          <p style={{fontSize:13,color:'var(--fd-muted)',lineHeight:1.7,margin:0}}>
            Describe tu problema. Nuestro sistema lo analizará automáticamente con IA.
          </p>

          {/* Consultar ticket */}
          <div className="fd-side-section" style={{borderTop:'none',paddingTop:0}}>
          <div style={{height:1,background:'var(--fd-border)',margin:'20px 0'}}/>
            <div className="fd-side-section-title">{t('fd.have_ticket')}</div>
            <TrackForm />
          </div>
          </div>

          {/* Card inferior: tiempos + equipo */}
          <div className="fd-card fd-card-side">
          <div style={{fontSize:14,fontWeight:500,color:'var(--fd-text)',marginBottom:16}}>{t('fd.more_info')}</div>
          <div className="fd-side-section" style={{borderTop:'none',paddingTop:0,marginTop:0}}>
            <div className="fd-side-section-title">Tiempos de respuesta por prioridad</div>
            {[
              {prio:'critical', dot:'#B91C1C', label:t('priority.critical')},
              {prio:'high',     dot:'#B45309', label:t('priority.high')},
              {prio:'medium',   dot:'#0085CA', label:t('priority.medium')},
              {prio:'low',      dot:'#15803D', label:t('priority.low')},
            ].map(s => {
              const d = slaData?.[s.prio];
              const overloaded = d && d.load_factor > 1.5;
              return (
                <div key={s.prio} style={{display:'flex',alignItems:'center',gap:8,fontSize:12,marginBottom:8}}>
                  <span style={{width:8,height:8,borderRadius:'50%',background:s.dot,flexShrink:0}}/>
                  <span style={{color:'var(--fd-text)',flex:1}}>{s.label}</span>
                  <span style={{fontWeight:700,color:overloaded?'#B91C1C':s.dot}}>
                    {d?.estimated_min ? fmtMin(d.estimated_min) : '…'}
                  </span>
                </div>
              );
            })}
            <div style={{fontSize:10,color:'#9CA3AF',marginTop:6,paddingTop:6,borderTop:'none',display:'flex',alignItems:'center',gap:4}}>
              <MIcon name="science" size={12} style={{marginRight:4}}/> Estimación beta · puede variar
            </div>
          </div>

          {/* Datos del equipo — oculto temporalmente, ver SHOW_DEVICE_INFO */}
          {SHOW_DEVICE_INFO && (
          <div className="fd-side-section">
            <div className="fd-side-section-title" style={{display:'flex',alignItems:'center',gap:6}}>
              Datos del equipo
              {deviceStatus==='agent' && <span style={{fontSize:9,padding:'1px 6px',background:'#DCFCE7',color:'#15803D',borderRadius:10,border:'1px solid #86EFAC',textTransform:'none',letterSpacing:0,fontWeight:600}}>✓ Agente</span>}
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:16}}>
              {[
                ['Equipo', deviceInfo?.hostname, true],
                ['IP',     deviceInfo?.local_ip, true],
                ['MAC',    deviceInfo?.mac,       true],
                ['OS',     deviceInfo?.os,        false],
                ['Modelo', deviceInfo?.model,     false],
                ['RAM',    deviceInfo?.ram_gb ? deviceInfo.ram_gb+'GB' : null, false],
              ].map(([l,v,mono]) => (
                <div key={l} style={{display:'flex',alignItems:'baseline',gap:8}}>
                  <span style={{fontSize:9,fontWeight:600,letterSpacing:.3,textTransform:'uppercase',color:'var(--fd-muted)',flexShrink:0,width:52}}>{l}</span>
                  <span title={v||''} style={{fontSize:11,color:v?(mono?'var(--fd-accent)':'var(--fd-text)'):'#B8BEC9',fontFamily:mono?'monospace':'inherit',fontWeight:mono&&v?600:400,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {deviceStatus==='loading'?'…':(v||'—')}
                  </span>
                </div>
              ))}
            </div>
          </div>
          )}


          </div>
        </div>

        {/* ── CARD DERECHO ANCHO — FORMULARIO ── */}
        <div className="fd-col-right fd-card fd-card-main">
          <div className="fd-form-header">
            <h2 style={{fontSize:16,fontWeight:600,color:'var(--fd-text)',marginBottom:3}}>
              {form.subcategory || form.category || t('fd.new_ticket_title')}
            </h2>
          </div>

          <form onSubmit={submit}>


            {/* Datos personales */}
            <div style={{padding:'20px 24px 0'}}>
              <div className="fd-section-title">{t('fd.your_data')}</div>
              {fdUser ? (
                <div style={{fontSize:13,color:'var(--fd-muted)',padding:'10px 14px',background:'#F8FBFD',borderRadius:'var(--radius-sm)',display:'flex',alignItems:'center',gap:8}}>
                  <MIcon name="check_circle" size={16} style={{color:'#15803D'}}/>
                  Se registrará a tu nombre: <strong style={{color:'var(--fd-text)'}}>{fdUser.name}</strong> ({fdUser.email})
                </div>
              ) : (
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',columnGap:16,rowGap:16}}>
                  <FloatInput label={t('fd.full_name')} required id="requester"
                    value={form.requester} onChange={e=>set('requester',e.target.value)}
                    autoComplete="name"/>
                  <FloatInput label={t('fd.contact_email')} required id="requester_email" type="email"
                    value={form.requester_email} onChange={e=>set('requester_email',e.target.value)}
                    autoComplete="email"/>
                </div>
              )}
              <div style={{marginTop:14}}>
                <WatcherEmailsField value={form.watcher_emails} onChange={v=>set('watcher_emails',v)} />
              </div>
            </div>

            {/* Título + IA — no aplica a peticiones estructuradas (alta de usuario, extensión, evento...) */}
            {!isPetition && (
            <div style={{padding:'20px 24px 0'}}>
              <div className="fd-section-title">{t('fd.the_problem')}</div>
              <div style={{position:'relative',marginBottom:10}}>
                <FloatInput label={t('fd.title_field')} required id="title" value={form.title} onChange={e=>set('title',e.target.value)} maxLength={200} />
                {aiLoading && (
                  <div style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',zIndex:1}}>
                    <Icon name="autorenew" size={16} style={{color:"var(--fd-accent)",animation:"spin .7s linear infinite"}} />
                  </div>
                )}
              </div>

              {aiSuggestion && !aiApplied && (
                <div style={{marginBottom:12,padding:'12px 14px',background:'#F8FBFF',border:'1px solid #E1F0FB',borderRadius:'var(--radius-sm)'}}>
                  <div style={{fontSize:11,fontWeight:700,color:'var(--fd-accent)',marginBottom:8}}><MIcon name="auto_awesome" size={13} style={{marginRight:4}}/> Análisis automático con IA</div>
                  <div className="fd-ai-grid" style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:10}}>
                    {[
                      ['Categoría', `${aiSuggestion.category} › ${aiSuggestion.subcategory}`],
                      ['Impacto', IMPACT_INFO.find(i=>i.v===aiSuggestion.impact)?.label || aiSuggestion.priority],
                      ['Título sugerido', aiSuggestion.suggested_title || aiSuggestion.category],
                    ].map(([l,v])=>(
                      <div key={l}>
                        <div style={{fontSize:10,color:'var(--fd-muted)',textTransform:'uppercase',letterSpacing:.3}}>{l}</div>
                        <div style={{fontSize:12,fontWeight:600,color:'var(--fd-text)',marginTop:2}}>{v}</div>
                      </div>
                    ))}
                  </div>
                  
                  <Button variant="primary" type="button" onClick={applyAI} style={{height:32}}>
                    ✓ Aplicar sugerencias de IA
                  </Button>
                </div>
              )}

            </div>
            )}

            {/* Impacto — no aplica a peticiones estructuradas ni a Accesos */}
            {!hideImpact && (
            <div style={{padding:'20px 24px 0'}}>
              <div className="fd-section-title">
                Indica el impacto en tu trabajo <span style={{color:'var(--fd-danger)'}}>*</span>
              </div>
              <div className="fd-impact-grid" style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:6}}>
                {IMPACT_INFO.map(imp => (
                  <button key={imp.v} type="button" onClick={() => set('impact', imp.v)}
                    style={{padding:'8px 10px',borderRadius:'var(--radius-sm)',border:`1.5px solid ${form.impact===imp.v ? imp.color : '#E0E0E0'}`,background: form.impact===imp.v ? imp.bg : '#fff',textAlign:'left',cursor:'pointer',transition:'all .15s'}}>
                    <div style={{display:'flex',alignItems:'flex-start',gap:5,marginBottom:3}}>
                      <span style={{width:7,height:7,borderRadius:'50%',background:imp.dot,flexShrink:0,marginTop:2}}/>
                      <span style={{fontSize:11,fontWeight:600,color:imp.color,lineHeight:1.3,minHeight:'2.6em',display:'block'}}>{imp.label}</span>
                    </div>
                    <div style={{fontSize:10,color:'var(--fd-muted)',lineHeight:1.3}}>{imp.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            )}

            {/* Categoría + campos extra — si categoría ya llegó precargada desde una
                card, no se muestran los selects (el usuario ya eligió esto), solo
                una confirmación + los campos específicos de esa solicitud */}
            <div style={{padding:'20px 24px 0'}}>
              <div className="fd-section-title">
                Información adicional
              </div>
              {prefilledCategory && prefilledSubcategory ? (
                <div style={{fontSize:13,color:'var(--fd-muted)',padding:'10px 14px',background:'#F8FBFD',borderRadius:'var(--radius-sm)',display:'flex',alignItems:'center',gap:8,marginBottom:16}}>
                  <MIcon name="check_circle" size={16} style={{color:'#15803D'}}/>
                  Solicitud: <strong style={{color:'var(--fd-text)'}}>{form.subcategory}</strong> ({form.category})
                </div>
              ) : null}
              {/* Categoría / subcategoría: fila propia y fija — nunca se mueve
                  aunque cambie el número de campos extra de abajo */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',columnGap:16,rowGap:16,marginBottom: (form.subcategory && EXTRA_FIELDS[form.subcategory]) ? 16 : 0}}>
                {!prefilledCategory && (
                  <FloatSelect label={t('fd.problem_type')} id="category"
                    value={form.category} onChange={e=>set('category',e.target.value)}>
                    <option value=""></option>
                    {Object.keys(CATEGORIES).map(cat=><option key={cat} value={cat}>{cat}</option>)}
                  </FloatSelect>
                )}
                {!prefilledSubcategory && form.category && (
                  <FloatSelect label="Problema específico" id="subcategory"
                    value={form.subcategory} onChange={e=>set('subcategory',e.target.value)}>
                    <option value=""></option>
                    {CATEGORIES[form.category]?.map(s=><option key={s} value={s}>{s}</option>)}
                  </FloatSelect>
                )}
              </div>

              {/* Campos específicos de la subcategoría elegida — grid aparte,
                  no comparte fila con el selector de arriba */}
              {form.subcategory && EXTRA_FIELDS[form.subcategory] && (
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',columnGap:16,rowGap:16}}>
                  {EXTRA_FIELDS[form.subcategory].map(field => (
                    field.type === 'select' ? (
                      <FloatSelect key={field.key} label={field.label} id={field.key}
                        value={form.extra_fields[field.key] || ''}
                        onChange={e => set('extra_fields', {...form.extra_fields, [field.key]: e.target.value})}>
                        <option value=""></option>
                        {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </FloatSelect>
                    ) : (
                      <FloatInput key={field.key} label={field.label} id={field.key}
                        type={field.type && field.type !== 'text' ? field.type : undefined}
                        value={form.extra_fields[field.key] || ''}
                        onChange={e => set('extra_fields', {...form.extra_fields, [field.key]: e.target.value})} />
                    )
                  ))}
                </div>
              )}
            </div>

            {/* Descripción + zona drop pegada */}
            <div style={{padding:'20px 24px 0'}}>  
              <FloatTextarea label={t('fd.describe')} id="description" rows={3} value={form.description} onChange={e=>set('description',e.target.value)} />
              <FdDropBar files={attachFiles} onChange={setAttachFiles}/>
            </div>
            {/* Submit */}
            <div style={{padding:'16px 24px 24px'}}>
              <div style={{display:'flex',justifyContent:'flex-end',gap:12}}>
                <Button variant="secondary" type="button"
                  onClick={() => { setForm({title:'',description:'',category:originalCategory,subcategory:originalSubcategory,impact:null,priority:'medium',requester:'',requester_email:'',watcher_emails:'',tags:[],extra_fields:{}}); setAiSuggestion(null); setAiApplied(false); }}>
                  Limpiar
                </Button>
                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? 'Enviando…' : 'Enviar'}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      
    </div>
  );
}

const SNACKBAR_COLORS = {
  success:  { bg:'#F0FDF4', border:'#86EFAC', text:'#15803D', iconBg:'#DCFCE7' },
  critical: { bg:'#FEF2F2', border:'#FECACA', text:'#B91C1C', iconBg:'#FEE2E2' },
  warning:  { bg:'#FFFBEB', border:'#FDE68A', text:'#B45309', iconBg:'#FEF3C7' },
  info:     { bg:'#EFF6FF', border:'#BFDBFE', text:'#1D4ED8', iconBg:'#DBEAFE' },
};

function Snackbar({ visible, color='info', icon, onClose, children }) {
  const col = SNACKBAR_COLORS[color] || SNACKBAR_COLORS.info;
  return (
    <div style={{
      position:'fixed', bottom:24, right:24, zIndex:9999,
      background: col.bg,
      border: `1px solid ${col.border}`,
      borderRadius:8,
      boxShadow:'0 2px 12px rgba(0,0,0,.08)',
      padding:'10px 12px 10px 14px',
      display:'flex', alignItems:'center', gap:10,
      fontSize:13, color: col.text,
      fontFamily:'var(--font-body)',
      transition:'opacity .25s, transform .25s',
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(10px)',
      maxWidth:320, minWidth:220,
      pointerEvents: visible ? 'auto' : 'none',
    }}>
      <span style={{width:18,height:18,borderRadius:'50%',background:col.iconBg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,flexShrink:0,fontWeight:700}}>
        {icon}
      </span>
      <span style={{flex:1,lineHeight:1.4,fontSize:12}}>{children}</span>
      <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:col.text,padding:'0 2px',fontSize:14,lineHeight:1,flexShrink:0,opacity:.6}}>✕</button>
    </div>
  );
}

function ErrorToast({ msg, onDone }) {
  const [visible, setVisible] = React.useState(true);
  React.useEffect(() => {
    const t = setTimeout(() => { setVisible(false); setTimeout(onDone, 300); }, 4000);
    return () => clearTimeout(t);
  }, [msg]);
  return (
    <Snackbar visible={visible} color="critical" icon="!" onClose={() => { setVisible(false); setTimeout(onDone, 300); }}>
      {msg}
    </Snackbar>
  );
}

function AiToast({ onDone }) {
  const [visible, setVisible] = React.useState(true);
  React.useEffect(() => {
    const t = setTimeout(() => { setVisible(false); setTimeout(onDone, 300); }, 3000);
    return () => clearTimeout(t);
  }, []);
  return (
    <Snackbar visible={visible} color="success" icon="✓" onClose={() => { setVisible(false); setTimeout(onDone, 300); }}>
      Clasificación aplicada — categoría, impacto y prioridad actualizados con IA
    </Snackbar>
  );
}

function SuccessView({ ticket, onNew, onTrack }) {
  const [toast, setToast] = React.useState(true);
  React.useEffect(() => {
    const t = setTimeout(() => setToast(false), 4000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="fd-root">
      <Snackbar visible={toast} color="success" icon="✓" onClose={() => setToast(false)}>
        Ticket enviado correctamente — te hemos enviado confirmación por email
      </Snackbar>
      <header className="fd-appbar">
        <div style={{display:'flex',alignItems:'center',gap:0}}>
          <LogoUNEA height={32} style={{display:'block'}} />
          <div style={{width:1,height:28,background:'var(--fd-border)',margin:'0 16px'}}/>
          <span style={{fontSize:15,fontWeight:400,color:'var(--fd-text)'}}>HelpDesk TI</span>
        </div>
      </header>

      <div className="fd-two-col" style={{alignItems:'start'}}>
        {/* Card izquierdo — info del agente */}
        <div className="fd-card fd-card-side">
          <h2 style={{fontSize:20,fontWeight:600,color:'var(--fd-text)',marginBottom:8,lineHeight:1.3}}>
            Tu solicitud está<br/>
            <em style={{fontStyle:'normal',color:'var(--fd-accent)'}}>en proceso</em>
          </h2>
          <p style={{fontSize:13,color:'var(--fd-muted)',lineHeight:1.7,marginBottom:20}}>
            Hemos recibido tu ticket y ha sido clasificado automáticamente.
          </p>

          <div className="fd-side-section">
            <div className="fd-side-section-title">Referencia</div>
            <div style={{fontSize:18,fontWeight:700,color:'var(--fd-accent)',letterSpacing:1,fontFamily:'monospace',marginBottom:8}}>
              {ticket.ref}
            </div>
            {ticket.requester_email && (
              <div style={{fontSize:12,color:'var(--fd-muted)'}}>
                Confirmación enviada a<br/>
                <strong style={{color:'var(--fd-text)'}}>{ticket.requester_email}</strong>
              </div>
            )}
          </div>

          <div className="fd-side-section">
            <div className="fd-side-section-title">Agente asignado</div>
            {ticket.assigned_to ? (
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <div style={{width:36,height:36,borderRadius:'50%',background:'var(--una-blue-lt)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,fontWeight:700,color:'var(--fd-accent)',flexShrink:0}}>
                  {ticket.assigned_to[0]?.toUpperCase()}
                </div>
                <div>
                  <div style={{fontSize:13,fontWeight:600,color:'var(--fd-text)'}}>{ticket.assigned_to}</div>
                  <div style={{fontSize:11,color:'var(--fd-muted)',display:'flex',alignItems:'center',gap:4}}>
                    <span style={{width:6,height:6,borderRadius:'50%',background:'#15803D',display:'inline-block'}}/>
                    Conectado
                  </div>
                </div>
              </div>
            ) : (
              <div style={{display:'flex',alignItems:'center',gap:8,fontSize:13,color:'var(--fd-muted)'}}>
                <div style={{width:36,height:36,borderRadius:'50%',background:'#F5F5F5',border:'1px dashed #E0E0E0',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16}}>
                  ⏳
                </div>
                <div>
                  <div style={{color:'var(--fd-text)',fontWeight:500}}>Pendiente de asignación</div>
                  <div style={{fontSize:11}}>Un agente revisará tu ticket en breve</div>
                </div>
              </div>
            )}
          </div>

          <div className="fd-side-section">
            <div className="fd-side-section-title">¿Qué pasa después?</div>
            <ol style={{paddingLeft:16,display:'flex',flexDirection:'column',gap:6}}>
              {[['Recepción','La IA ha clasificado tu ticket.'],['Asignación','Se asigna al agente adecuado.'],['Resolución','Te mantienen informado por email.'],['Cierre','Confirmamos la solución contigo.']].map(([t,d])=>(
                <li key={t} style={{fontSize:12,color:'var(--fd-muted)',lineHeight:1.5}}>
                  <strong style={{color:'var(--fd-text)'}}>{t}</strong> — {d}
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Card derecho — resumen del ticket */}
        <div className="fd-card fd-card-main">
          <div className="fd-form-header">
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
              <span style={{width:8,height:8,borderRadius:'50%',background:'#15803D'}}/>
              <h2 style={{fontSize:16,fontWeight:600,color:'var(--fd-text)'}}>¡Ticket enviado con éxito!</h2>
            </div>
            <p style={{fontSize:12,color:'var(--fd-muted)'}}>Tu solicitud ha sido registrada y clasificada automáticamente.</p>
          </div>

          <div style={{padding:'20px 24px',display:'flex',flexDirection:'column',gap:16}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',columnGap:16,rowGap:16}}>
              <div className="fd-summary-field">
                <div className="fd-summary-label">Referencia</div>
                <div className="fd-summary-value" style={{fontFamily:'monospace',color:'var(--fd-accent)',fontWeight:700}}>{ticket.ref}</div>
              </div>
              <div className="fd-summary-field">
                <div className="fd-summary-label">Prioridad</div>
                <div className="fd-summary-value">{{ low:'Baja', medium:'Media', high:'Alta', critical:'Crítica' }[ticket.priority] || ticket.priority}</div>
              </div>
              <div className="fd-summary-field">
                <div className="fd-summary-label">Solicitante</div>
                <div className="fd-summary-value">{ticket.requester}</div>
              </div>
              <div className="fd-summary-field">
                <div className="fd-summary-label">Estado</div>
                <div className="fd-summary-value" style={{display:'flex',alignItems:'center',gap:6}}>
                  <span style={{width:6,height:6,borderRadius:'50%',background:'#0085CA'}}/>
                  Abierto
                </div>
              </div>
            </div>

            <div className="fd-summary-field">
              <div className="fd-summary-label">Título</div>
              <div className="fd-summary-value">{ticket.title}</div>
            </div>

            {ticket.description && (
              <div className="fd-summary-field">
                <div className="fd-summary-label">Descripción</div>
                <div className="fd-summary-value" style={{fontSize:13,color:'var(--fd-muted)',lineHeight:1.6,whiteSpace:'pre-wrap'}}>{ticket.description}</div>
              </div>
            )}

            <div style={{display:'flex',justifyContent:'flex-end',gap:12,marginTop:24}}>
              <Button variant="primary" onClick={onTrack}>Seguir mi ticket</Button>
              <Button variant="secondary" onClick={onNew}>Crear otro ticket</Button>
            </div>
          </div>
        </div>
      </div>

      
    </div>
  );
}
